import { type IdentifierSystem, identifierSystem } from "./index";
export const PATIENT_HOLDING_KEY_SYSTEM = identifierSystem("patient-registration-holding");
export const REGISTRATION_REVIEW_SOURCE_SYSTEM = identifierSystem("registration-review-source");
export const APP_PATIENT_SOURCE_SYSTEM = identifierSystem("app-patient");

/**
 * Every `Resource.identifier[].system` this codebase writes. ADR-0022.
 *
 * Three kinds live here and the distinction matters when reading a mapper:
 *
 * - **Entity identity** — the aggregate's own persistence-independent id (ADR-0020).
 * - **Member identity** — a part's id, scoped by its parent aggregate.
 * - **Relationship key** — a value both writers can compute *before* either writes,
 *   so a conditional create can race safely. `ifNoneExist` is a server-side search,
 *   so a freshly minted uuid can never serve; these are a unique index spelled in
 *   FHIR, and are deliberately not the entity identity.
 */

// ── Entity identity ──
export const PERFORMANCE_MEASURE_ENTITY_ID_SYSTEM = identifierSystem("performance-measure");

export const ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM = identifierSystem("allergy-intolerance");
export const CARE_PLAN_ENTITY_ID_SYSTEM = identifierSystem("care-plan");
export const CASE_ENTITY_ID_SYSTEM = identifierSystem("case");
export const CLINICAL_NOTE_ENTITY_ID_SYSTEM = identifierSystem("clinical-note");
export const CONSENT_ENTITY_ID_SYSTEM = identifierSystem("consent");
export const CONDITION_ENTITY_ID_SYSTEM = identifierSystem("condition");
export const DEVICE_ENTITY_ID_SYSTEM = identifierSystem("device");
export const DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM = identifierSystem("diagnostic-procedure");
export const EXAM_ENTITY_ID_SYSTEM = identifierSystem("exam");
export const FINDING_ENTITY_ID_SYSTEM = identifierSystem("finding");
export const FLAG_ENTITY_ID_SYSTEM = identifierSystem("flag");
export const FLOW_INSTANCE_ENTITY_ID_SYSTEM = identifierSystem("flow-instance");
export const HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM = identifierSystem("healthcare-service");
export const LOCATION_ENTITY_ID_SYSTEM = identifierSystem("location");
export const MEDICATION_STATEMENT_ENTITY_ID_SYSTEM = identifierSystem("medication-statement");
export const OBSERVATION_ENTITY_ID_SYSTEM = identifierSystem("observation");
export const ORDER_SET_ENTITY_ID_SYSTEM = identifierSystem("order-set");
export const OUTBOUND_MESSAGE_ENTITY_ID_SYSTEM = identifierSystem("outbound-message");
export const PATIENT_DOCUMENT_ENTITY_ID_SYSTEM = identifierSystem("patient-document");
export const PATIENT_ENTITY_ID_SYSTEM = identifierSystem("patient");
export const PRACTITIONER_ENTITY_ID_SYSTEM = identifierSystem("practitioner");
export const QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM = identifierSystem("questionnaire-instance");
export const SCHEDULE_ENTITY_ID_SYSTEM = identifierSystem("schedule");
export const TASK_ENTITY_ID_SYSTEM = identifierSystem("task");
export const TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM = identifierSystem("treatment-procedure");
export const VISIT_ENTITY_ID_SYSTEM = identifierSystem("visit");

export const RELATED_PERSON_ENTITY_ID_SYSTEM = identifierSystem("related-person");

// ── Member identity ──
export const CARE_PLAN_GOAL_MEMBER_ID_SYSTEM = identifierSystem("care-plan-goal-member");
export const SCHEDULE_BLOCK_ID_SYSTEM = identifierSystem("schedule-block");
export const TREATMENT_OBSERVATION_ID_SYSTEM = identifierSystem("treatment-observation");
export const DIAGNOSTIC_TRIAL_ID_SYSTEM = identifierSystem("diagnostic-trial");
export const TREATMENT_TRIAL_ID_SYSTEM = identifierSystem("treatment-trial");
export const TREATMENT_UNCAPTURED_ID_SYSTEM = identifierSystem("treatment-uncaptured-observation");
export const VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM = identifierSystem("visit-assignment");
export const VISIT_EXECUTION_MEMBER_ID_SYSTEM = identifierSystem("visit-execution");

// ── Relationship keys ──
/** One encounter note per visit. Holds the VisitId, not the note's own identity. */
export const VISIT_NOTE_KEY_SYSTEM = identifierSystem("visit-note-visit");
/**
 * One filed capture per file, per patient, per visit. The value is the scoped
 * content key, never a bare digest.
 *
 * It is an identifier because `Attachment.hash` has no search parameter in R4,
 * so a hash stored there cannot answer "is this file already filed" without a
 * custom SearchParameter to provision. `identifier` is a token parameter that
 * works today, and a content hash is a legitimate business identifier.
 */
export const OBSERVATION_CAPTURE_CONTENT_KEY_SYSTEM = identifierSystem("observation-capture-content");
export const NOTIFICATION_KEY_SYSTEM = identifierSystem("notification-key");
export const PREFS_IDENTIFIER_SYSTEM = identifierSystem("notify-prefs");
export const WORK_ITEM_KEY_SYSTEM = identifierSystem("stage-work-item");
export const RETIRED_KEY_SYSTEM = identifierSystem("retired-work-item");
export const DISCRIMINATOR_SYSTEM = identifierSystem("work-item-discriminator");
export const TASK_NUMBER_SYSTEM = identifierSystem("task-number");
export const LOCK_SESSION_SYSTEM = identifierSystem("lock-session");
export const LOCK_FORM_ID_SYSTEM = identifierSystem("lock-form-id");
/** What a chart lock coordinates on: `visit:<VisitId>` or `patient:<PatientId>`. */
export const LOCK_SCOPE_SYSTEM = identifierSystem("lock-scope");

/** Lineage and revision of a DeviceDefinition. The *value* is a canonical URL. */
export const DEVICE_DEFINITION_LINEAGE_SYSTEM = identifierSystem("device-definition-lineage");
export const DEVICE_DEFINITION_REVISION_SYSTEM = identifierSystem("device-definition-revision");

// ── Seeded identity ──
/** Deterministic slugs so a reseed rewrites the same rows rather than duplicating. */
export const FIXTURE_IDENTIFIER_SYSTEM = identifierSystem("fixture");
export const CANONICAL_IDENTIFIER_SYSTEM = identifierSystem("canonical");

// ── External systems ──
/** The clinician's Google Workspace login, impersonated to mint Meet spaces. */
export const GOOGLE_WORKSPACE_EMAIL_SYSTEM = identifierSystem("google-workspace-email");
export const GHL_OPPORTUNITY_SYSTEM = identifierSystem("ghl-opportunity");

/** The bot-owned Practitioner that system writes are attributed to. */
export const SYSTEM_PRACTITIONER_SYSTEM = identifierSystem("system-practitioner");

/** Every system above, for exhaustive checks and test sweeps. */
export const IDENTIFIER_SYSTEMS: readonly IdentifierSystem[] = [
  REGISTRATION_REVIEW_SOURCE_SYSTEM,
  PATIENT_HOLDING_KEY_SYSTEM,
  APP_PATIENT_SOURCE_SYSTEM,
  ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM,
  CARE_PLAN_ENTITY_ID_SYSTEM,
  CASE_ENTITY_ID_SYSTEM,
  CLINICAL_NOTE_ENTITY_ID_SYSTEM,
  CONDITION_ENTITY_ID_SYSTEM,
  DEVICE_ENTITY_ID_SYSTEM,
  DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  EXAM_ENTITY_ID_SYSTEM,
  FLAG_ENTITY_ID_SYSTEM,
  FLOW_INSTANCE_ENTITY_ID_SYSTEM,
  HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM,
  LOCATION_ENTITY_ID_SYSTEM,
  MEDICATION_STATEMENT_ENTITY_ID_SYSTEM,
  OBSERVATION_ENTITY_ID_SYSTEM,
  ORDER_SET_ENTITY_ID_SYSTEM,
  OUTBOUND_MESSAGE_ENTITY_ID_SYSTEM,
  PATIENT_DOCUMENT_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  PRACTITIONER_ENTITY_ID_SYSTEM,
  QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM,
  PERFORMANCE_MEASURE_ENTITY_ID_SYSTEM,
  SCHEDULE_ENTITY_ID_SYSTEM,
  TASK_ENTITY_ID_SYSTEM,
  TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
  CARE_PLAN_GOAL_MEMBER_ID_SYSTEM,
  SCHEDULE_BLOCK_ID_SYSTEM,
  TREATMENT_OBSERVATION_ID_SYSTEM,
  TREATMENT_TRIAL_ID_SYSTEM,
  DIAGNOSTIC_TRIAL_ID_SYSTEM,
  TREATMENT_UNCAPTURED_ID_SYSTEM,
  VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM,
  VISIT_EXECUTION_MEMBER_ID_SYSTEM,
  VISIT_NOTE_KEY_SYSTEM,
  OBSERVATION_CAPTURE_CONTENT_KEY_SYSTEM,
  NOTIFICATION_KEY_SYSTEM,
  PREFS_IDENTIFIER_SYSTEM,
  WORK_ITEM_KEY_SYSTEM,
  RETIRED_KEY_SYSTEM,
  DISCRIMINATOR_SYSTEM,
  TASK_NUMBER_SYSTEM,
  LOCK_SESSION_SYSTEM,
  LOCK_FORM_ID_SYSTEM,
  LOCK_SCOPE_SYSTEM,
  DEVICE_DEFINITION_LINEAGE_SYSTEM,
  DEVICE_DEFINITION_REVISION_SYSTEM,
  FIXTURE_IDENTIFIER_SYSTEM,
  CANONICAL_IDENTIFIER_SYSTEM,
  GOOGLE_WORKSPACE_EMAIL_SYSTEM,
  GHL_OPPORTUNITY_SYSTEM,
  SYSTEM_PRACTITIONER_SYSTEM,
];

export const FINDING_DEFINITION_LINEAGE_SYSTEM = identifierSystem("finding-definition-lineage");
export const FINDING_DEFINITION_REVISION_SYSTEM = identifierSystem("finding-definition-revision");
